import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Modal, Form, Input, Space, Popconfirm, message, Switch, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import authService from '../utils/auth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

export default function StoreManagement() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/stores`, { headers: authService.getAuthHeaders() });
      setList(res.data.data || []);
    } catch (err) {
      message.error('获取门店列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const openModal = (record) => {
    setEditing(record || null);
    form.resetFields();
    if (record) form.setFieldsValue(record);
    else form.setFieldsValue({ isActive: true });
    setOpen(true);
  };

  const handleSubmit = async (values) => {
    try {
      if (editing) {
        await axios.put(`${API_BASE}/stores/${editing.id}`, values, { headers: authService.getAuthHeaders() });
        message.success('已更新');
      } else {
        await axios.post(`${API_BASE}/stores`, values, { headers: authService.getAuthHeaders() });
        message.success('已创建');
      }
      setOpen(false);
      fetchList();
    } catch (err) {
      message.error(err.response?.data?.error || '保存失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE}/stores/${id}`, { headers: authService.getAuthHeaders() });
      message.success('已删除');
      fetchList();
    } catch (err) {
      message.error(err.response?.data?.error || '删除失败');
    }
  };

  const columns = [
    { title: '门店名称', dataIndex: 'name', key: 'name' },
    { title: '地址', dataIndex: 'address', key: 'address' },
    /*
     * 2026-10-08 去掉了银行四列。收款账号已拆成独立实体，
     * 在会员后台「销售与门店 → 收款账号」维护，不再挂在门店上。
     */
    { title: '状态', dataIndex: 'isActive', key: 'isActive', render: (v) => v ? <Tag color="green">启用</Tag> : <Tag>停用</Tag> },
    {
      title: '操作', key: 'actions', width: 180,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openModal(r)}>编辑</Button>
          <Popconfirm title="确认删除该门店？" onConfirm={() => handleDelete(r.id)} okText="删除" cancelText="取消">
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 16 }}>
      <Card
        title="门店管理"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => openModal(null)}>新建门店</Button>}
      >
        <Table rowKey="id" loading={loading} columns={columns} dataSource={list} size="small" />
      </Card>

      <Modal
        title={editing ? '编辑门店' : '新建门店'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        destroyOnClose
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="门店名称" rules={[{ required: true, message: '请输入门店名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="address" label="地址">
            <Input />
          </Form.Item>
          <Form.Item name="notes" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="isActive" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
